import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "@shadcn-in-nx/ui/scroll-area";
import { Button } from "@shadcn-in-nx/ui/button";
import { ChevronLeft, Copy, MessageSquare, CheckCircle2, XCircle, Clock, AlertTriangle, Check } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useChatContext } from "../ChatContext";
import ActivityConsoleOutput from "./ActivityConsoleOutput";
import ActivityTechnicalDetails from "./ActivityTechnicalDetails";

const METHOD_TONES = {
    GET: "bg-gray-100 text-gray-700",
    POST: "bg-blue-50 text-blue-700",
    PUT: "bg-violet-50 text-violet-700",
    PATCH: "bg-amber-50 text-amber-700",
    DELETE: "bg-red-50 text-red-700",
};

function ActivityDetailPanel({ invocation, onBack }) {
    const { t } = useTranslation();
    const { setPrefillMessage, setAppViewMode } = useChatContext();
    const [copied, setCopied] = useState(false);

    const { call: fetchDetail, response: detailResponse, isLoading: loadingDetail } =
        useBluelagoon({ method: "getFunctionInvocationById" });

    useEffect(() => {
        if (invocation?.id && invocation?.created_at) {
            fetchDetail({ invocationId: invocation.id, createdAt: invocation.created_at });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invocation?.id]);

    // Merge: light list fields (immediate) with full detail (async). Full wins when loaded.
    const inv = { ...invocation, ...(detailResponse?.data?.invocation || {}) };
    const statusCode = parseInt(inv.status_code, 10);
    const isFailed = inv.status === "FAILURE" || statusCode >= 400;
    const isThrottled = inv.was_throttled === "true" || inv.status === "THROTTLED";
    const method = (inv.method || "GET").toUpperCase();
    const StatusIcon = isFailed ? XCircle : isThrottled ? Clock : CheckCircle2;
    const statusTone = isFailed
        ? "text-red-700 bg-red-50 border-red-200"
        : isThrottled
          ? "text-amber-700 bg-amber-50 border-amber-200"
          : "text-green-700 bg-green-50 border-green-200";
    const accent = isFailed ? "bg-red-500" : isThrottled ? "bg-amber-500" : "bg-green-500";

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(inv.request_id || inv.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    }, [inv]);

    const handleAskAI = useCallback(() => {
        const logs = (() => {
            const raw = inv.console_output;
            if (!raw) return [];
            if (Array.isArray(raw)) return raw;
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        })();

        const sections = [
            `My API function ${method} ${inv.path} is failing. Can you help me fix it?`,
            "",
            "Request:",
            `- Method: ${method}`,
            `- Path: ${inv.path}`,
            `- Status: HTTP ${inv.status_code || "—"}`,
            `- Duration: ${parseInt(inv.duration, 10) || 0}ms`,
            `- When: ${inv.created_at ? new Date(inv.created_at).toISOString() : "—"}`,
            `- Request ID: ${inv.request_id || inv.id || "—"}`,
        ];

        if (inv.error_message) {
            sections.push("", "Error:", "```", inv.error_message, "```");
        }

        if (logs.length > 0) {
            const lines = logs.slice(-30).map((log) => {
                const ts = log.ts ? new Date(log.ts).toISOString().slice(11, 19) : "--:--:--";
                const tag = (log.type || "log").toUpperCase();
                return `[${ts}] ${tag}: ${log.message || ""}`;
            });
            sections.push("", `Console output (last ${lines.length} of ${logs.length}):`, "```", ...lines, "```");
        }

        setPrefillMessage(sections.join("\n"));
        setAppViewMode("chat");
        onBack();
    }, [inv, method, setPrefillMessage, setAppViewMode, onBack]);

    return (
        <ScrollArea className="h-full bg-gray-50">
            <div className="p-4 md:p-6 pb-8">
                <div className="max-w-4xl mx-auto space-y-6">
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-gray-600 hover:text-gray-900" onClick={onBack}>
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        {t("common.back") || "Back"}
                    </Button>
                    <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 overflow-hidden">
                        <span className={`absolute left-0 top-0 bottom-0 w-1 ${accent}`} />
                        <div className="flex items-center flex-wrap gap-2 mb-3">
                            <span className={`font-mono text-xs font-semibold px-2 py-1 rounded ${METHOD_TONES[method] || METHOD_TONES.GET}`}>{method}</span>
                            <span className="font-mono text-base text-gray-900 font-medium break-all">{inv.path}</span>
                        </div>
                        <div className="flex items-center flex-wrap gap-3">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${statusTone}`}>
                                <StatusIcon className="w-3.5 h-3.5" />
                                {isFailed ? t("activity.failed") : isThrottled ? t("activity.tooBusy") : t("activity.worked")}
                            </span>
                            <span className="font-mono text-sm text-gray-700">{parseInt(inv.duration, 10)}ms</span>
                            <span className="font-mono text-xs text-gray-400">HTTP {inv.status_code}</span>
                        </div>
                    </div>
                    {isFailed && inv.error_message && (
                        <div className="bg-white rounded-lg shadow-sm border border-red-200 overflow-hidden">
                            <div className="flex items-center gap-2 px-6 py-3 bg-red-50 border-b border-red-200">
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                                <p className="text-sm font-semibold text-red-900">{t("activity.whatHappened") || "What went wrong"}</p>
                            </div>
                            <pre className="bg-gray-900 text-red-300 text-xs font-mono p-6 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">{inv.error_message}</pre>
                        </div>
                    )}
                    <ActivityConsoleOutput raw={inv.console_output} loading={loadingDetail} />
                    <ActivityTechnicalDetails inv={inv} loading={loadingDetail} />
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleCopy}>
                            {copied ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                            {copied ? (t("common.copied") || "Copied") : (t("activity.copyRequestId"))}
                        </Button>
                        {isFailed && (
                            <Button size="sm" onClick={handleAskAI} className="bg-blue-600 hover:bg-blue-700">
                                <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                                {t("activity.askAiToFix")}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </ScrollArea>
    );
}

export default ActivityDetailPanel;
